import React, { useState, useMemo } from "react";
import { Icon, Button } from "semantic-ui-react";
import { Link, useLocation } from "react-router-dom";
import { useAuth, usePermissions } from "../../../../hooks";
import { topMenuItems, menuItemsWithSections } from "../adminMenuConfig.ts";
import "./MobileMenu.scss";

interface MobileMenuProps {
    darkLightTheme: boolean;
}

export function MobileMenu({ darkLightTheme }: MobileMenuProps) {
    const [isOpen, setIsOpen] = useState(false);
    const { pathname } = useLocation();
    const { logout } = useAuth();
    const { canAccess, isSuperAdmin, loading: permissionsLoading } = usePermissions();

    const filteredMenuItems = useMemo(() => {
        if (permissionsLoading) return [];
        
        const topFiltered = topMenuItems.filter((item) => !item.module || canAccess(item.module));
        
        const sectionsFiltered = menuItemsWithSections
            .map(group => ({
                ...group,
                items: group.items.filter(item => {
                    if (group.name === "Mantenimiento" && !isSuperAdmin) {
                        return false;
                    }
                    return !item.module || canAccess(item.module);
                })
            }))
            .filter(group => group.items.length > 0)
            .flatMap(group => group.items);

        return [...topFiltered, ...sectionsFiltered];
    }, [canAccess, permissionsLoading, isSuperAdmin]);

    const isCurrentPath = (path: string | (string | RegExp)[]): boolean => {
        if (pathname === '/admin') return false;
        if (path === pathname) return true;
        if (Array.isArray(path)) {
            return path.some(p =>
                typeof p === 'string' ? pathname.includes(p) : p.test(pathname)
            );
        } else {
            return typeof path === 'string' ? pathname.includes(path) : false;
        }
    };

    const toggleMenu = () => {
        setIsOpen(!isOpen);
    };

    const closeMenu = () => {
        setIsOpen(false);
    };

    const onLogout = async () => {
        closeMenu();
        await logout();
    };

    return (
        <div className={`admin-mobile-menu-container ${isOpen ? 'menu-open' : ''}`}>
            {/* Hamburger Button */}
            <Button
                size="big"
                className="admin-mobile-menu__toggle"
                icon={isOpen ? "close" : "bars"}
                onClick={toggleMenu}
            />

            {/* Overlay */}
            {isOpen && (
                <div
                    className="admin-mobile-menu__overlay"
                    onClick={closeMenu}
                />
            )}

            {/* Mobile Menu Dropdown */}
            <div
                className={`admin-mobile-menu ${isOpen ? 'admin-mobile-menu--open' : ''} ${darkLightTheme ? 'dark' : 'light'}`}
            >
                <nav className="admin-mobile-menu__nav">
                    {filteredMenuItems.map(item => (
                        <Link
                            key={item.name}
                            to={item.to}
                            className={`admin-mobile-menu__nav-item ${isCurrentPath(item.pathMatch || item.to) ? 'active' : ''}`}
                            onClick={closeMenu}
                        >
                            <Icon name={item.icon as any} />
                            <span>{item.name}</span>
                        </Link>
                    ))}
                </nav>
            </div>
        </div>
    );
}
